import batch from 'function-batch';
import { Logger } from './logger';
import {
    CONNECT,
    CURRENT_STATE,
    DISCONNECT,
    GET_CURRENT,
    GET_HISTORY,
    HISTORY,
    STATE_UPDATE,
} from './webSocketMiddleware.constants';
import { DELIBERATE_CLOSE } from './webSocket.utils';

const actions = {
    TURBINE: {
        batchAction: batch((payload, dispatch) => dispatch({ type: 'turbines/loadMultiple', payload })),
        historyAction: 'turbines/updateOneHistory',
        platformAction: 'offshorePlatforms/updateOne',
    },
    TURBINE_INFO: {
        batchAction: batch((payload, dispatch) =>
            dispatch({ type: 'turbines/loadMultipleNotesAndException', payload })
        ),
    },
    GRID_CONNECTION: {
        batchAction: batch((payload, dispatch) => dispatch({ type: 'powerGrids/loadMultiple', payload })),
        historyAction: 'powerGrids/loadOneHistory',
    },
    POWER_PLANT: {
        batchAction: batch((payload, dispatch) => dispatch({ type: 'plantData/loadMultiple', payload })),
        historyAction: 'plantData/loadHistory',
    },
    WEATHER: {
        action: 'weather/load',
        historyAction: 'weather/loadHistory',
    },
    HISTORICAL_PRODUCTION_FORECAST: {
        action: 'powerGrids/loadForecastHistory',
        historyAction: 'powerGrids/loadForecastHistory',
    },
    ALARM: {
        batchAction: batch((payload, dispatch) => dispatch({ type: 'logTabs/loadMultipleAlarms', payload })),
    },
    ROP: {
        batchAction: batch((payload, dispatch) => dispatch({ type: 'logTabs/loadMultipleLogs', payload })),
    },
    VISITOR_LOG: {
        batchAction: batch((payload, dispatch) => dispatch({ type: 'logTabs/loadMultipleLogs', payload })),
    },
    PERSON: {
        batchAction: batch((payload, dispatch) => dispatch({ type: 'logTabs/loadMultipleVisitors', payload })),
        historyAction: batch((payload, dispatch) => {
            const { entityKey, entityType, last_update_time, command } = payload;
            dispatch({
                type: 'logTabs/loadMultipleVisitors',
                payload: [
                    {
                        entityKey,
                        entityType,
                        last_update_time,
                        command,
                        ...payload.value,
                    },
                ],
            });
        }),
    },
    WORK_ORDER: {
        batchAction: batch((payload, dispatch) => dispatch({ type: 'logTabs/loadMultipleWorkOrders', payload })),
    },

    PRODUCTION_FORECAST: { action: 'powerGrids/loadForecast' },
    POWER_PRICE: { action: 'plantData/loadPricing' },
    PERF_METRICS: { action: 'plantData/addPerformanceMetrics' },
    WEATHER_FORECAST: { action: 'weather/loadForecast' },
    FEED_STATUS: { action: 'systemStatus/systemsStatusLoad' },
    LIGHTNING_ALERT: { action: 'notifications/addLightningAlert' },
    VESSEL: { action: 'vesselData/setVesselData' },
};

/* eslint-disable consistent-return, camelcase  */

export const actionDispatcher = (dispatch, data) => {
    const { command, entityKey, entityType, last_update_time, fields, values } = data;

    if (!entityKey || !entityType || !actions[entityType]) {
        /* Backend will soon filter out entities we don't need. */
        Logger.log(
            'Following entity from WebSocket not recognised:',
            entityKey,
            entityType,
            actions[entityType],
            typeof data,
            data
        );
        return;
    }

    switch (command) {
        case HISTORY: {
            if (entityType === 'PERSON') {
                const payload = {
                    entityKey,
                    entityType,
                    last_update_time,
                    command,
                    ...values,
                };

                actions[entityType].batchAction(payload, dispatch);
            } else
                dispatch({
                    type: actions[entityType].historyAction,
                    payload: data,
                });
            break;
        }
        case STATE_UPDATE: {
            const payload = {
                entityKey,
                entityType,
                last_update_time,
                ...fields,
            };
            if (actions[entityType].batchAction) {
                actions[entityType].batchAction(payload, dispatch);
            } else {
                if (entityType === 'VESSEL') {
                    dispatch({ type: 'vesselData/setVesselData', payload });
                }

                dispatch({
                    type: /^OSP.?$/.test(entityKey) ? actions[entityType].platformAction : actions[entityType].action,
                    payload,
                });
            }
            break;
        }
        default:
            Logger.error('Unknown message received', data);
    }
};

let webSocket;

const isRunningInCypress = () => !!window.Cypress;

const getWebSocket = (url) => {
    return isRunningInCypress() ? new window.MockedWebSocket(url) : new window.WebSocket(url);
};
const addQueryParam = (url, key, value) => {
    if (!value) return url;

    const parsedUrl = new URL(url);
    parsedUrl.searchParams.set(key, value);
    return parsedUrl.toString();
};

export const webSocketMiddleware = (store) => (next) => (action) => {
    switch (action.type) {
        case CONNECT: {
            webSocket = getWebSocket(addQueryParam(action.URL, 'code', localStorage.getItem('auth_code')));
            store.dispatch({ type: 'webSocket/isConnecting' });
            webSocket.onopen = () => {
                store.dispatch({
                    type: 'webSocket/requestCurrentState',
                });
                store.dispatch({ type: 'webSocket/isConnected' });
            };
            // isDisconnected should have reconnect true, except when we are manually closing
            webSocket.onclose = (closeEvent) => {
                if (closeEvent.code !== DELIBERATE_CLOSE) {
                    Logger.warn('WS closed with event:', closeEvent);
                    store.dispatch({ type: 'webSocket/isDisconnected' });
                }
            };
            webSocket.onerror = (error) => {
                Logger.warn('WS error:', error);
                store.dispatch({ type: 'webSocket/handleError' }, error);
            };
            webSocket.onmessage = ({ data }) => actionDispatcher(store.dispatch, JSON.parse(data));

            break;
        }
        case DISCONNECT: {
            return webSocket ? webSocket.close(DELIBERATE_CLOSE, 'Deliberate disconnection') : null;
        }
        case GET_CURRENT: {
            if (webSocket.readyState !== 1) return;
            return webSocket.send(
                JSON.stringify({
                    command: CURRENT_STATE,
                    entityType: '*',
                    entityKey: '*',
                })
            );
        }
        case GET_HISTORY: {
            const { entityType, entityKey, count, precision, fields } = action.payload;
            if (webSocket.readyState !== 1) return;

            return webSocket.send(
                JSON.stringify({
                    command: HISTORY,
                    entityType,
                    entityKey,
                    count,
                    precision,
                    fields,
                })
            );
        }
        default:
            return next(action);
    }
};
